import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import apiClient from "../api-client";
import { useAuth } from "@/hooks/use-auth";
import type {
  ProjectCardWithPermissionsDto,
  ProjectDto,
} from "./dtos/project.dto";
import type { PortfolioCardWithPermissionsDto } from "./dtos/portfolio.dto";
import {
  UpdateUserSchema,
  type UpdateUserInput,
  type UserCardDto,
  type UserInterest,
} from "./dtos/user.dto";
import { validateSchema } from "./dtos/validation";
import type { StateCode } from "../states";
import type { TeamSummaryDto } from "./dtos/team.dto";
import type { ExternalIds } from "./dtos/user.dto";
import type { AccessRequest } from "./dtos/access-request.dto";
import type { SearchPageEnvelopeDto } from "./dtos/search-relaxation.dto";

export const userKeys = {
  all: ["user"] as const,
  list: (...args: (string | string[] | number | boolean | undefined)[]) =>
    [...userKeys.all, "list", ...args] as const,
  listCount: (...args: (string | string[] | number | boolean | undefined)[]) =>
    [...userKeys.all, "list-count", ...args] as const,
  detail: (id?: string) => [...userKeys.all, "detail", id] as const,
  projects: (id?: string) => [...userKeys.all, "projects", id] as const,
  portfolios: (id?: string) => [...userKeys.all, "portfolios", id] as const,
  accessRequests: (id?: string) =>
    [...userKeys.all, "access-requests", id] as const,
  resourceAccessRequests: (id?: string) =>
    [...userKeys.all, "resource-access-requests", id] as const,
  teams: (id?: string) => [...userKeys.all, "teams", id] as const,
  institutions: (id?: string) => [...userKeys.all, "institutions", id] as const,
  resourceAccessRequest: (
    resource: "project" | "portfolio" | "challenge",
    id?: string,
  ) => [...userKeys.all, "resource-access-request", resource, id] as const,
  fundraiser: (id?: string) => [...userKeys.all, "fundraiser", id] as const,
};

export interface User {
  id: string;
  name: string;
  email?: string;
  /**
   * Viewer-only (present when fetching your own user). `null` means the user
   * has never been through onboarding — the guard funnels them to /onboarding.
   * `undefined` (older backend / non-viewer) must NOT trigger the funnel.
   */
  onboardingCompletedAt?: string | null;
  /** 0–100 profile completeness, drives the dashboard checklist card. */
  profileCompletenessScore?: number;
  handle: string | null;
  avatar: string | null;
  about: string | null;
  profileUrl: string | null;
  mainInstitutionId: string | null;
  mainInstitution: {
    id: string;
    name: string;
  } | null;
  interests: UserInterest[];
  externalIds: ExternalIds;
  fundraiserId: string | null;
  settings: {
    followingListPublic: boolean;
    donationsEnabled: boolean;
  };
  claim: { maskedEmail: string; setEmail: boolean } | null;
  topics: string[];
}

/**
 * Legacy: search users by query string
 * Uses: GET /users?q=<query>
 */
export function useUsers(options?: {
  query?: string;
  institutionIds?: string[] | undefined;
  states?: StateCode[] | undefined;
  interest?: UserInterest | undefined;
  limit?: number;
}) {
  const { query, institutionIds, states, interest, limit = 20 } = options ?? {};
  return useInfiniteQuery({
    queryKey: userKeys.list(query, institutionIds, states, interest, limit),
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
      const response = await apiClient.get<{
        results: UserCardDto[];
        pageInfo: { nextCursor?: string };
      }>(`/users`, {
        params: {
          q: query?.trim() || undefined,
          "institutionId[]": institutionIds,
          "state[]": states,
          interest: interest,
          cursor: pageParam,
          limit: limit,
        },
      });
      return response.data;
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.pageInfo.nextCursor,
  });
}

/**
 * Legacy: count users
 * Uses: GET /users/count
 */
export function useUsersCount(options?: {
  query?: string;
  institutionIds?: string[] | undefined;
  states?: StateCode[] | undefined;
  interest?: UserInterest | undefined;
}) {
  const { query, institutionIds, states, interest } = options ?? {};
  return useQuery({
    queryKey: userKeys.listCount(query, institutionIds, states, interest),
    queryFn: async () => {
      const response = await apiClient.get<{
        count: number;
      }>(`/users/count`, {
        params: {
          q: query?.trim() || undefined,
          "institutionId[]": institutionIds,
          "state[]": states,
          interest: interest,
        },
      });
      return response.data.count;
    },
  });
}

/**
 * v2 search: structured filters + relaxation envelope
 * Uses: GET /users/v2
 */
export function useUsersSearch(options?: {
  query?: string;
  institutionIds?: string[] | undefined;
  excludedInstitutionIds?: string[] | undefined;
  states?: StateCode[] | undefined;
  personIds?: string[] | undefined;
  excludedPersonIds?: string[] | undefined;
  portfolioIds?: string[] | undefined;
  excludedPortfolioIds?: string[] | undefined;
  challengeIds?: string[] | undefined;
  excludedChallengeIds?: string[] | undefined;
  interest?: UserInterest | undefined;
  type?: "RESEARCHER" | "FUNDER" | undefined;
  /** MEMBER = joined Catalyze; INDEXED = imported from public data. */
  membership?: "MEMBER" | "INDEXED" | undefined;
  /** HIGH hides indexed profiles without a photo plus a bio or publications. */
  quality?: "HIGH" | undefined;
  limit?: number;
  enabled?: boolean;
}) {
  const {
    query,
    institutionIds,
    excludedInstitutionIds,
    states,
    personIds,
    excludedPersonIds,
    portfolioIds,
    excludedPortfolioIds,
    challengeIds,
    excludedChallengeIds,
    interest,
    type,
    membership,
    quality,
    limit = 20,
    enabled = true,
  } = options ?? {};
  return useInfiniteQuery({
    queryKey: userKeys.list(
      "v2",
      query,
      institutionIds,
      excludedInstitutionIds,
      states,
      personIds,
      excludedPersonIds,
      portfolioIds,
      excludedPortfolioIds,
      challengeIds,
      excludedChallengeIds,
      interest,
      type,
      membership,
      quality,
      limit,
    ),
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
      const response = await apiClient.get<SearchPageEnvelopeDto<UserCardDto>>(
        `/users/v2`,
        {
          params: {
            q: query?.trim() || undefined,
            "institutionId[]": institutionIds,
            "excludeInstitutionId[]": excludedInstitutionIds,
            "state[]": states,
            "personId[]": personIds,
            "excludePersonId[]": excludedPersonIds,
            "portfolioId[]": portfolioIds,
            "excludePortfolioId[]": excludedPortfolioIds,
            "challengeId[]": challengeIds,
            "excludeChallengeId[]": excludedChallengeIds,
            interest: interest,
            type,
            membership,
            quality,
            cursor: pageParam,
            limit: limit,
          },
        },
      );
      return response.data;
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.pageInfo.nextCursor,
    enabled,
  });
}

export function useUser(userId?: string) {
  return useQuery({
    queryKey: userKeys.detail(userId),
    queryFn: async (): Promise<User> => {
      const response = await apiClient.get(`/users/${userId}`);
      return response.data;
    },
    enabled: !!userId,
  });
}

export function useUserProjects(userId: string) {
  return useQuery({
    queryKey: userKeys.projects(userId),
    queryFn: async (): Promise<ProjectCardWithPermissionsDto[]> => {
      const response = await apiClient.get(`/users/${userId}/projects`);
      return response.data;
    },
    enabled: !!userId,
  });
}

export function useUserPortfolios(userId: string) {
  return useQuery({
    queryKey: userKeys.portfolios(userId),
    queryFn: async (): Promise<PortfolioCardWithPermissionsDto[]> => {
      const response = await apiClient.get(`/users/${userId}/portfolios`);
      return response.data;
    },
    enabled: !!userId,
  });
}

/**
 * Update an existing user
 * Uses: PATCH /users/:id
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      data,
      avatar,
    }: {
      userId: string;
      avatar?: File | null;
      data: UpdateUserInput;
    }) => {
      const dto = validateSchema(UpdateUserSchema, data);
      const response = await apiClient.patch<User>(`/users/${userId}`, dto);
      if (avatar) {
        const formData = new FormData();
        formData.append("avatar", avatar);
        const response = await apiClient.patch<User>(
          `/users/${userId}/avatar`,
          formData,
        );
        return response.data;
      }
      return response.data;
    },
    onSuccess: (data, { userId }) => {
      queryClient.setQueryData(userKeys.detail(userId), data);
    },
  });
}

/**
 * Mark the signed-in user's onboarding as completed (idempotent server-side).
 * Uses: POST /users/me/onboarding/complete — returns the fresh viewer user,
 * which is written into the cache BEFORE navigation so the guard doesn't
 * bounce back to /onboarding on a stale user.
 */
export function useCompleteOnboarding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<User>(
        `/users/me/onboarding/complete`,
      );
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(userKeys.detail(data.id), data);
    },
  });
}

export interface UserAccessRequest {
  id: string;
  userId: string;
  message: string;
  status: "REQUESTED" | "ALLOWED" | "DENIED";
  createdAt: string;
  updatedAt: string;
  projectId: string;
  project: Pick<ProjectDto, "id" | "title" | "snippet">;
}

export interface UserResourceAccessRequest {
  id: string;
  message: string;
  status: "REQUESTED" | "ALLOWED" | "DENIED";
  updatedAt: string;
  resource: { id: string; title: string };
}

export interface UserAccessRequests {
  project: UserResourceAccessRequest[];
  portfolio: UserResourceAccessRequest[];
  challenge: UserResourceAccessRequest[];
}

/**
 * Fetch user's document access requests
 * Uses: GET /users/:id/projects-document-access-requests
 */
export function useMyDocumentAccessRequests() {
  const { user } = useAuth();
  return useQuery({
    queryKey: userKeys.accessRequests(user?.id),
    queryFn: async () => {
      const response = await apiClient.get<UserAccessRequest[]>(
        `/users/${user?.id}/projects-document-access-requests`,
      );
      return response.data;
    },
    enabled: !!user?.id,
  });
}

/**
 * Fetch user's resource (project/portfolio/challenge) access requests
 * Uses: GET /users/:id/access-requests
 */
export function useMyResourceAccessRequests() {
  const { user } = useAuth();
  return useQuery({
    queryKey: userKeys.resourceAccessRequests(user?.id),
    queryFn: async () => {
      const response = await apiClient.get<UserAccessRequests>(
        `/users/${user?.id}/access-requests`,
      );
      return response.data;
    },
    enabled: !!user?.id,
  });
}

/**
 * Fetch user's resource (project/portfolio/challenge) access requests
 * Uses: GET /<resource>s/:id/access-requests/status
 */
export function useMyResourceAccessRequest(
  resource: "project" | "portfolio" | "challenge",
  id: string,
) {
  const { user } = useAuth();
  return useQuery({
    queryKey: userKeys.resourceAccessRequest(resource, id),
    queryFn: async () => {
      const response = await apiClient.get<AccessRequest>(
        `/${resource}s/${id}/access-requests/status`,
      );
      return response.data;
    },
    enabled: !!user?.id,
  });
}

export function useClaimProfile() {
  return useMutation({
    mutationFn: async ({
      userId,
      email,
    }: {
      userId: string;
      email?: string;
    }) => {
      await apiClient.post(`/users/${userId}/claim`, { email });
    },
  });
}

/**
 * Finalize the caller's own claim: an invited applicant who set a password via
 * their claim link flips their account from claimable to claimed. Idempotent.
 * Uses: POST /users/me/claim/finalize
 */
export function useFinalizeClaim() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await apiClient.post(`/users/me/claim/finalize`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}

export function useUserTeams(userId?: string) {
  return useQuery({
    queryKey: userKeys.teams(userId),
    queryFn: async () => {
      const response = await apiClient.get<TeamSummaryDto[]>(
        `/users/${userId}/teams`,
      );
      return response.data;
    },
    enabled: !!userId,
  });
}

export function useUserInstitutions(userId?: string) {
  return useQuery({
    queryKey: userKeys.institutions(userId),
    queryFn: async () => {
      const response = await apiClient.get<{ id: string; name: string }[]>(
        `/users/${userId}/institutions`,
      );
      return response.data;
    },
    enabled: !!userId,
  });
}
